import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import {
  LayoutDashboard,
  CalendarDays,
  Layers,
  User,
  Activity,
  BookOpen,
  Settings,
  Route,
  Ellipsis,
} from "lucide-react";
import { TOKENS } from './ui/dashboard-primitives'
import { EASE_OUT } from '../lib/motion'

interface NavItem {
  path: string;
  icon: typeof LayoutDashboard;
  label: string;
  /** What the phone's bottom bar prints under the icon, where a tab is about 72 px wide. The accessible
   *  name stays `label`, which contains it (WCAG 2.5.3, label in name). */
  short: string;
  group: "primary" | "bottom";
  /** On the phone: a tab of its own, or one of the destinations behind the bar's "More" tab. */
  tab: boolean;
}

const navItems: NavItem[] = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard", short: "Dashboard", group: "primary", tab: true },
  { path: "/sessions", icon: CalendarDays, label: "Sessions", short: "Sessions", group: "primary", tab: true },
  { path: "/builder", icon: Layers, label: "Session Builder", short: "Builder", group: "primary", tab: true },
  { path: "/journey", icon: Route, label: "Journey", short: "Journey", group: "primary", tab: true },
  { path: "/profile", icon: User, label: "Profile", short: "Profile", group: "primary", tab: false },
  { path: "/knowledge-base", icon: BookOpen, label: "Knowledge Base", short: "Knowledge Base", group: "primary", tab: false },
  { path: "/settings", icon: Settings, label: "Settings", short: "Settings", group: "bottom", tab: false },
];

const primaryItems = navItems.filter((i) => i.group === "primary");
const bottomItems = navItems.filter((i) => i.group === "bottom");
// Apple's tab bar holds five and never scrolls: four tabs, and "More" for the rest (X-11).
const tabItems = navItems.filter((i) => i.tab);
const moreItems = navItems.filter((i) => !i.tab);

// ── Style tokens ────────────────────────────────────────────────────

// Solid, not --warm-dark: page content scrolls under both bars (fonts.css, --chrome-surface).
const RAIL_SURFACE: React.CSSProperties = {
  background: "var(--chrome-surface)",
  border: "1px solid " + TOKENS.creamBorder,
  boxShadow: "0 18px 48px -16px rgba(20, 14, 12, 0.55), inset 0 1px 0 " + TOKENS.creamHairline,
};

const HAIRLINE_TOP: React.CSSProperties = {
  background: "var(--chrome-surface)",
  borderTop: "1px solid " + TOKENS.creamBorder,
  boxShadow: "0 -12px 32px -12px rgba(20, 14, 12, 0.55)",
};

// Full background tint on active — not a side-stripe.
// eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
const ACTIVE_CLASSES = "text-[var(--accent-amber)] bg-[rgba(232,197,160,0.16)]";
const OPEN_CLASSES = "text-[var(--text-cream)] bg-(--text-cream-hairline)";
const IDLE_CLASSES = "text-[var(--text-cream-soft)] hover:text-[var(--text-cream)] hover:bg-(--text-cream-hairline)";

// The list opens upward, so its shadow falls upward like the bar's own (HAIRLINE_TOP), not down onto the bar
// the way the rail's does: over the coach dock it is the same solid chrome, and the shadow is what separates them.
const MORE_PANEL: React.CSSProperties = {
  ...RAIL_SURFACE,
  boxShadow: "0 -14px 36px -12px rgba(20, 14, 12, 0.6), inset 0 1px 0 " + TOKENS.creamHairline,
};

function navItemClasses(active: boolean) {
  // Focus ring per project a11y norm.
  return [
    "relative inline-flex items-center justify-center w-12 h-12 rounded-xl",
    "transition-colors duration-200 ease-out",
    "outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber) focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
    active ? ACTIVE_CLASSES : IDLE_CLASSES,
  ].join(" ");
}

/** A tab of the phone's bottom bar: an icon over its label, 48 px tall, an equal share of the width. The
 *  keyboard ring is the base `:focus-visible` rule (`styles/theme.css`). */
function tabClasses(active: boolean, open = false) {
  return [
    "flex-1 min-w-0 h-12 rounded-xl flex flex-col items-center justify-center gap-0.5",
    "transition-colors duration-200 ease-out",
    // The tiles touch, so the ring is drawn inside each one: outside it would run 4 px into the next tile.
    "focus-visible:-outline-offset-2",
    // The page you are on is the one amber tab. An open list is only pressed in: the tint of a hover, held.
    active ? ACTIVE_CLASSES : open ? OPEN_CLASSES : IDLE_CLASSES,
  ].join(" ");
}

const TAB_LABEL = "max-w-full truncate px-0.5 text-[0.6875rem] font-medium leading-4";

// ── Component ───────────────────────────────────────────────────────
//
// There is no sync control here. It was a button for the holders of a permission
// key (`deployment.sync.run`, which left the catalog with #388), with a progress bar
// and a status poll, that started the sync pipeline; the pipeline, its routes and
// the permission-gated button went together (#387, ADR 0012), because no platform is
// read any more. The button was drawn only for that group, so for everyone else the
// rail and the bottom bar are exactly what they were.

export function Sidebar() {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* ── Desktop vertical rail ──────────────────────────────────── */}
      <aside
        className="hidden sm:flex fixed left-4 top-4 bottom-4 w-[72px] rounded-3xl flex-col items-center py-6 z-50"
        style={RAIL_SURFACE}
        aria-label="Primary"
      >
        {/* Logo — bare amber glyph, no nested tile */}
        <Link
          to="/"
          aria-label="Groundwork home"
          className="mb-8 inline-flex items-center justify-center w-12 h-12 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-(--accent-amber) transition-colors"
          style={{ color: "var(--accent-amber)" }}
        >
          <Activity className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
        </Link>

        <nav aria-label="Primary navigation" className="flex-1 flex flex-col gap-3">
          {primaryItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                title={item.label}
                className={navItemClasses(active)}
              >
                <item.icon className="w-6 h-6" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
              </Link>
            );
          })}
        </nav>

        <div className="flex flex-col gap-3">
          {bottomItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                title={item.label}
                className={navItemClasses(active)}
              >
                <item.icon className="w-6 h-6" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </aside>

      {/* ── Mobile bottom bar ───────────────────────────────────────── */}
      {/* Four tabs and More, each an icon over a label, sharing the width: 65 px tall as before (the dock's
          offset in styles/index.css is measured from it) and never wider than the phone, so nothing scrolls. */}
      <aside
        className="sm:hidden fixed left-0 right-0 bottom-0 z-50"
        style={{
          ...HAIRLINE_TOP,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        aria-label="Primary"
      >
        <nav aria-label="Primary navigation" className="flex items-stretch px-1.5 py-2">
          {tabItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                className={tabClasses(active)}
              >
                <item.icon className="w-5 h-5" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
                <span className={TAB_LABEL}>{item.short}</span>
              </Link>
            );
          })}

          <MoreTab isActive={isActive} />
        </nav>
      </aside>
    </>
  );
}

// ── "More" ──────────────────────────────────────────────────────────

/**
 * The fifth tab: the destinations that have no tab of their own, in a short list above the bar.
 *
 * A disclosure, not a modal: a press elsewhere closes it, Escape closes it and puts focus back on the tab,
 * and the list follows the button in the document so Tab walks into it and out the other side (leaving it
 * closes it too). There is no scrim and nothing goes inert, because the page behind stays usable. A modal
 * would have to bring its own focus trap, and the app is moving every modal onto one shared Dialog.
 */
function MoreTab({ isActive }: { isActive: (path: string) => boolean }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Every navigation closes it: a destination chosen from the list, the page you are already on, Back. Keyed on
  // the location's own key, not the pathname: a pathname that came back round (open on "/", go to Settings, go
  // to "/") would match the one it was opened on and open the list by itself, and a tap on the page you are
  // already on would change nothing. The reset is made during render, which React allows for a component's own
  // state; an effect would draw one frame with the list still open.
  const [seenKey, setSeenKey] = useState(location.key);
  if (seenKey !== location.key) {
    setSeenKey(location.key);
    setOpen(false);
  }
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const behind = moreItems.some((item) => isActive(item.path));

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={wrapper}
      className="relative flex-1 min-w-0"
      // Tabbing out of the list closes it. `relatedTarget` is null when focus leaves the window, which
      // is not a reason to close.
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        if (next && !event.currentTarget.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={open ? "more-destinations" : undefined}
        // The page is one of the three behind this tab, so this is the tab that is "current".
        aria-current={behind ? "true" : undefined}
        onClick={() => setOpen((o) => !o)}
        className={`${tabClasses(behind, open)} w-full`}
      >
        <Ellipsis className="w-5 h-5" strokeWidth={behind ? 2 : 1.75} aria-hidden="true" />
        <span className={TAB_LABEL}>More</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="more-destinations"
            aria-label="More"
            // Out of the trigger it came from: the panel's corner is the tab's, and it grows from there.
            style={{ ...MORE_PANEL, transformOrigin: "bottom right" }}
            className="absolute right-0 bottom-full mb-3 w-60 max-w-[calc(100vw-1rem)] rounded-2xl p-1.5"
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.16, ease: EASE_OUT } }}
            exit={{ opacity: 0, y: 4, transition: { duration: 0.12, ease: EASE_OUT } }}
          >
            <ul className="flex flex-col">
              {moreItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      aria-current={active ? "page" : undefined}
                      className={`flex h-12 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors duration-200 ease-out focus-visible:-outline-offset-2 ${
                        active ? ACTIVE_CLASSES : IDLE_CLASSES
                      }`}
                    >
                      <item.icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </div>
  );
}
